/**
 * PRENUMERATIONENS DATALAGER — det skärmen och bannern läser och trycker genom.
 *
 * FYRA RUTTER, INGEN SYNC. Prenumerationen ligger i den DELADE databasen
 * (schema/d1.sql), inte i firmans Durable Object, så ingenting här går genom
 * SYNC_PULL. Det är fyra vanliga anrop:
 *   SUBSCRIPTION      GET, FULL_VIEW_ROLES, safe
 *   BILLING_PERIODS   GET, FULL_VIEW_ROLES, safe
 *   BILLING_CHECKOUT  POST, MANAGER_ONLY + ägaren, op_id
 *   BILLING_PORTAL    POST, MANAGER_ONLY + ägaren, op_id
 * ALLA FYRA ÄR locked_mode 'allowed' med flit: en firma i låsläge måste kunna
 * se sin prenumeration, stämma av sina perioder och TECKNA OM — annars är
 * låsläget slutstationen.
 *
 * PERIODERNA FÅR FALLA UTAN ATT SKÄRMEN GÖR DET. Läget och nästa dragning är
 * det chefen kom hit för; historiken är bokförarens avstämning. Ett fel på
 * BILLING_PERIODS ger därför `perioder: null` och en rad om det, i stället för
 * en tom skärm där betalknappen försvann.
 *
 * OP_ID MYNTAS AV ANROPAREN (beslut 09) och skickas HÄR bara vidare. Skärmen
 * fryser ett per knapp vid första trycket och skickar samma vid ett omtag —
 * annars blir varje omsändning en ny Stripe-session. Se Prenumeration.tsx.
 *
 * ADRESSEN ÖPPNAS SOM EN NAVIGERING, inte som en ny flik. Stripes kassa tar
 * över hela telefonen och skickar tillbaka kunden till /prenumeration när han
 * är klar (worker/src/modules/billing/subscription.ts). En popup hade dessutom
 * blockerats: trycket och anropet ligger inte i samma tick.
 */

import { useEffect, useState } from 'react';
import {
  BILLING_CHECKOUT,
  BILLING_PERIODS,
  BILLING_PORTAL,
  SUBSCRIPTION,
} from '@delat/routes';
import type {
  BillingPeriodRow,
  CompanyId,
  Millis,
  OpId,
  SubscriptionResponse,
} from '@delat/types';
import { granska, klienten, lyckades, misslyckades, nyUlid } from '../api';
import type { Klartidfel, Resultat } from '../api';
import {
  granskaFaktureringsperioder,
  granskaKassalank,
  granskaPortallank,
  granskaPrenumeration,
} from '../api/granskare/prenumeration';

/* == Datum ===============================================================
   visaKortDatum och visaPeriod BODDE HÄR och hör hemma i app/datum.ts, bredvid
   visaDag — nu står de där, och Profil.tsx läser visaDagMedAr ur samma fil i
   stället för att bygga ihop året med en egen slice(0,4).
   ======================================================================== */

/* == Frågesträngen efter Stripe ==========================================
   Servern skickar tillbaka kunden till /prenumeration?betalning=klar eller
   ?betalning=avbruten. FLAGGAN LÄSES HÄR OCH INGEN ANNANSTANS: app/navigering.tsx
   läser bara pathname och ska inte lära sig frågesträngar för en enda skärms
   skull.
   ======================================================================== */

export type Betalningsbesked = 'klar' | 'avbruten';

export const lasBetalningsflagga = (): Betalningsbesked | null => {
  try {
    const flagga = new URLSearchParams(globalThis.location.search).get('betalning');
    return flagga === 'klar' || flagga === 'avbruten' ? flagga : null;
  } catch {
    return null;
  }
};

/* == Spärren mot en ANDRA teckning =======================================
   BLOCKERANDE OM DEN INTE FINNS, och det är värt att skriva ut varför.

   Efter en lyckad kassa kan Stripes webhook dröja. Under det fönstret står
   subscription.status kvar på 'trialing' med stripe_subscription_id = NULL, och
   SERVERNS ENDA SPÄRR (worker/src/modules/billing/subscription.ts rad 112:
   `stripe_subscription_id !== null && status !== 'locked'`) släpper därför
   igenom ett andra BILLING_CHECKOUT. Resultatet blir en ANDRA prenumeration hos
   Stripe på samma kund; planCheckoutCompleted skriver sedan över kolumnen med
   den nya, och den första blir en osynlig, evigt debiterande föräldralös cykel
   som varken skärmen eller ROW_DUPLICATE-spärren någonsin ser igen.

   Flaggan ?betalning=klar räcker inte som spärr: den läses EN gång och plockas
   direkt ur adressen, så en omladdning — pull-to-refresh på telefonen, vilket är
   precis vad skärmen själv uppmanar till — gav en ny knapp "Teckna Klartid".
   Stämpeln nedan ligger i telefonen och överlever därför omladdningen.

   PER FIRMA, med tid, och den släpper av sig själv: en webhook som ALDRIG kommer
   får inte låsa ägaren ute från kassan för alltid.
   ======================================================================== */

/** Så länge en genomförd kassa spärrar en andra teckning. */
export const KASSAFONSTER_MS = 15 * 60_000;

const kassanyckel = (firmaId: CompanyId): string => `klartid.kassa.${firmaId}`;

export const markeraNyssBetalat = (firmaId: CompanyId): void => {
  try {
    globalThis.localStorage?.setItem(kassanyckel(firmaId), String(Date.now()));
  } catch {
    /* Utan lagring är vi tillbaka på flaggan i adressen. Inte värre. */
  }
};

export const glomNyssBetalat = (firmaId: CompanyId): void => {
  try {
    globalThis.localStorage?.removeItem(kassanyckel(firmaId));
  } catch {
    /* Se ovan. */
  }
};

/** true = en kassa gick igenom för mindre än KASSAFONSTER_MS sedan. */
export const nyssBetalat = (firmaId: CompanyId): boolean => {
  try {
    const ratext = globalThis.localStorage?.getItem(kassanyckel(firmaId)) ?? null;
    if (ratext === null) return false;
    const tid = Number(ratext);
    return Number.isFinite(tid) && tid > 0 && Date.now() - tid < KASSAFONSTER_MS;
  } catch {
    return false;
  }
};

/**
 * Tar bort flaggan ur adressen UTAN att lägga en post i historiken, så att en
 * omladdning inte upprepar beskedet. Sökvägen lämnas orörd — navigeringen har
 * redan matchat den, och ett byte här hade flyttat användaren.
 */
export const nollaBetalningsflagga = (): void => {
  try {
    const params = new URLSearchParams(globalThis.location.search);
    if (!params.has('betalning')) return;
    params.delete('betalning');
    const kvar = params.toString();
    const adress =
      kvar === '' ? globalThis.location.pathname : `${globalThis.location.pathname}?${kvar}`;
    globalThis.history.replaceState(null, '', adress);
  } catch {
    /* Utan History-API står flaggan kvar och beskedet upprepas. Inte värre. */
  }
};

/* == Läsning ============================================================= */

export interface Prenumerationsvy {
  readonly prenumeration: SubscriptionResponse;
  /** null = perioderna gick inte att hämta. Se filhuvudet. */
  readonly perioder: readonly BillingPeriodRow[] | null;
}

export type Prenumerationslage =
  | { readonly status: 'laddar' }
  | { readonly status: 'fel'; readonly fel: Klartidfel }
  | { readonly status: 'klar'; readonly vy: Prenumerationsvy };

/**
 * Läget och historiken, laddade om när `omgang` ändras. Anropen går parallellt:
 * de rör inte varandra, och skärmen visar ingenting förrän det viktiga av dem
 * har svarat.
 */
export const usePrenumerationen = (
  firmaId: CompanyId,
  omgang: number,
): Prenumerationslage => {
  const [lage, sattLage] = useState<Prenumerationslage>({ status: 'laddar' });

  useEffect(() => {
    const styrning = new AbortController();
    let aktuell = true;
    sattLage({ status: 'laddar' });

    void (async () => {
      const [prenumeration, perioder] = await Promise.all([
        klienten.anrop(
          SUBSCRIPTION,
          { params: { company_id: firmaId }, signal: styrning.signal },
          granskaPrenumeration,
        ),
        klienten.anrop(
          BILLING_PERIODS,
          { params: { company_id: firmaId }, signal: styrning.signal },
          granskaFaktureringsperioder,
        ),
      ]);
      if (!aktuell) return;
      if (!prenumeration.ok) {
        sattLage({ status: 'fel', fel: prenumeration.fel });
        return;
      }
      sattLage({
        status: 'klar',
        vy: {
          prenumeration: prenumeration.varde,
          perioder: perioder.ok ? perioder.varde.periods : null,
        },
      });
    })();

    return () => {
      aktuell = false;
      styrning.abort();
    };
  }, [firmaId, omgang]);

  return lage;
};

/**
 * BARA ANKARET, för bannern på startsidorna.
 *
 * `hamta` är falskt i det normala läget — en firma som betalar ska inte kosta
 * ett extra anrop varje gång startsidan öppnas. Bannern slår på det bara när
 * sessionen säger 'trialing', för då är datumet hela beskedet.
 * ETT FEL SYNS INTE. Kan vi inte hämta ankaret visas ingen banner alls; att
 * skriva "provperioden tar slut, vet inte när" hjälper ingen.
 */
export const useProvperiodensSlut = (firmaId: CompanyId, hamta: boolean): Millis | null => {
  const [slut, sattSlut] = useState<Millis | null>(null);

  useEffect(() => {
    if (!hamta) {
      sattSlut(null);
      return;
    }
    const styrning = new AbortController();
    let aktuell = true;

    void (async () => {
      const svar = await klienten.anrop(
        SUBSCRIPTION,
        { params: { company_id: firmaId }, signal: styrning.signal },
        granskaPrenumeration,
      );
      if (!aktuell || !svar.ok) return;
      sattSlut(svar.varde.subscription.current_period_end);
    })();

    return () => {
      aktuell = false;
      styrning.abort();
    };
  }, [firmaId, hamta]);

  return slut;
};

/* == Skrivning: de två knapparna ========================================= */

export const nyttOpId = (): OpId => granska.opId(nyUlid(), 'op_id');

/**
 * FÖRSTA TECKNINGEN eller OMTECKNINGEN EFTER ETT LÅS. Servern avgör vilket och
 * skickar med rätt ankare till Stripe; klienten skickar ingen kropp alls.
 * En LÖPANDE prenumeration som tecknas igen ger ROW_DUPLICATE — därför visar
 * skärmen knappen bara i 'trialing' och 'locked'.
 */
export const tecknaPrenumerationen = async (
  firmaId: CompanyId,
  op_id: OpId,
): Promise<Resultat<string>> => {
  const svar = await klienten.anrop(
    BILLING_CHECKOUT,
    { params: { company_id: firmaId }, op_id },
    granskaKassalank,
  );
  if (!svar.ok) return misslyckades(svar.fel);
  return lyckades(svar.varde.url);
};

/**
 * Stripes betalningssida: där avslutas prenumerationen, och där byts kortet.
 * BETALNINGSDAGEN GÅR INTE ATT ÄNDRA DÄR (beslut 18) — ankaret är låst för en
 * löpande prenumeration, och omteckning efter ett lås är enda vägen till ett
 * nytt.
 */
export const oppnaBetalningssidan = async (
  firmaId: CompanyId,
  op_id: OpId,
): Promise<Resultat<string>> => {
  const svar = await klienten.anrop(
    BILLING_PORTAL,
    { params: { company_id: firmaId }, op_id },
    granskaPortallank,
  );
  if (!svar.ok) return misslyckades(svar.fel);
  return lyckades(svar.varde.url);
};

/** Hela telefonen går till Stripe. Granskaren har redan prövat protokollet. */
export const gaTillBetalningen = (adress: string): void => {
  globalThis.location.assign(adress);
};
